'use client'

import * as React from 'react'

import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Chip } from '@/components/form/chip'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'

import { useDebounce } from '@/hooks/use-debouncer'
import { useDynamicQuery } from '@/hooks/use-dynamic-query'
import { useIntersectionObserver } from '@/hooks/use-intersection-observer'

import { cn } from '@/utils'

import type { MultiComboboxProps } from './multi-combobox-types'

import { filterItems, getItemLabel } from './utils'

export function MultiCombobox<T extends object>({
  classNames,
  closeOnSelect = false,
  customTrigger,
  disabled = false,
  getOptionLabel,
  infinite = false,
  initialItems,
  labelKey,
  labelKeys,
  onChange,
  onSearch,
  onSelectItem,
  options,
  placeholder,
  popoverContentProps,
  queryOptions,
  renderOption,
  renderSelected,
  searchDebounceMs = 300,
  value,
  valueKey
}: MultiComboboxProps<T>) {
  const t = useTranslations('components.form.autocomplete')
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')
  const [searchQuery, setSearchQuery] = React.useState('')

  const itemsCacheRef = React.useRef<Map<string, T>>(new Map())

  // Mirrors itemsCacheRef into state so cache updates (which happen in
  // effects, after render) can trigger a re-render to reflect resolved labels.
  const [cachedItemsMap, setCachedItemsMap] = React.useState<Map<string, T>>(
    () => {
      const map = new Map<string, T>()
      for (const item of initialItems ?? []) {
        const key = String(item[valueKey] ?? '')
        if (key) map.set(key, item as T)
      }
      return map
    }
  )

  React.useEffect(() => {
    for (const item of initialItems ?? []) {
      const key = String(item[valueKey] ?? '')
      if (key) itemsCacheRef.current.set(key, item as T)
    }
    setCachedItemsMap(new Map(itemsCacheRef.current))
  }, [initialItems, valueKey])

  const debouncedSearch = useDebounce((query: string) => {
    setSearchQuery(query)
    onSearch?.(query)
  }, searchDebounceMs)

  const handleSearch = React.useCallback(
    (query: string) => {
      setInputValue(query)
      debouncedSearch(query)
    },
    [debouncedSearch]
  )

  const handleToggle = React.useCallback(
    (itemValue: string, item?: T) => {
      const isSelected = value.includes(itemValue)
      const next = isSelected
        ? value.filter((v) => v !== itemValue)
        : [...value, itemValue]

      onChange?.(next)
      if (item) {
        onSelectItem?.(item, !isSelected)
      }
      if (closeOnSelect) {
        setOpen(false)
      }
    },
    [value, onChange, onSelectItem, closeOnSelect]
  )

  const handleRemoveChip = React.useCallback(
    (itemValue: string) => {
      onChange?.(value.filter((v) => v !== itemValue))
    },
    [value, onChange]
  )

  const lastItemRef = React.useRef<HTMLDivElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lastItemElement, setLastItemElement] =
    React.useState<HTMLDivElement | null>(null)

  const isStaticMode = !!options && !queryOptions

  const cacheItem = React.useCallback(
    (item: T) => {
      const v = String(item[valueKey] ?? '')
      if (v) itemsCacheRef.current.set(v, item)
    },
    [valueKey]
  )

  // Resolve selected values that aren't cached yet by asking the query hook
  // to fetch just those ids (batched into one call), instead of relying on
  // the searchable list to happen to contain them. Based on `cachedItemsMap`
  // state (not the current render's `items`) so it can be computed before
  // calling useDynamicQuery below.
  const missingIds = React.useMemo(
    () => value.filter((v) => !cachedItemsMap.has(v)),
    [value, cachedItemsMap]
  )

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    items: queryItems
  } = useDynamicQuery<T>({
    ids: missingIds,
    infinite,
    isStaticMode,
    queryOptions,
    searchQuery
  })

  const items = React.useMemo(() => {
    if (isStaticMode && options) {
      return filterItems(options, inputValue, labelKey, labelKeys)
    }
    return queryItems
  }, [isStaticMode, options, inputValue, labelKey, labelKeys, queryItems])

  React.useEffect(() => {
    items.forEach(cacheItem)
    setCachedItemsMap(new Map(itemsCacheRef.current))
  }, [items, cacheItem])

  useIntersectionObserver(lastItemRef, fetchNextPage, {
    enabled: infinite && open && items.length > 0,
    hasNextPage,
    isFetchingNextPage,
    itemsCount: items.length
  })

  return (
    <div className={cn('flex w-full flex-col gap-2', classNames?.wrapper)}>
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          {customTrigger ? (
            customTrigger
          ) : (
            <Button
              aria-expanded={open}
              className={cn(
                'h-[42px] w-full max-w-full min-w-0 justify-between rounded-md!',
                value.length > 0 ? 'text-foreground' : 'text-muted-foreground',
                classNames?.trigger
              )}
              disabled={disabled || isLoading}
              role="combobox"
              type="button"
              variant="outline"
              wrapChildren={false}
            >
              <span
                className={cn(
                  'min-w-0 flex-1 truncate text-start text-sm font-normal'
                )}
              >
                {value.length > 0
                  ? t('selectedCount', { count: value.length })
                  : placeholder || t('placeholder')}
              </span>
              {isLoading ? (
                <Loader2 className="ms-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
              ) : (
                <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
              )}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn(
            'w-(--radix-popover-trigger-width) min-w-56 p-0',
            classNames?.popoverContent
          )}
          {...popoverContentProps}
        >
          <Command
            shouldFilter={
              !infinite && !isStaticMode && typeof queryOptions !== 'function'
            }
          >
            <CommandInput
              onValueChange={
                infinite || isStaticMode || typeof queryOptions === 'function'
                  ? handleSearch
                  : undefined
              }
              placeholder={t('searchPlaceholder')}
              value={
                infinite || isStaticMode || typeof queryOptions === 'function'
                  ? inputValue
                  : undefined
              }
            />
            <CommandList>
              {isLoading && items.length === 0 ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-muted-foreground ms-2 text-sm">
                    {t('loading')}
                  </span>
                </div>
              ) : items.length === 0 ? (
                <CommandEmpty>{t('noItemsFound')}</CommandEmpty>
              ) : (
                <CommandGroup>
                  {items.map((item, index) => {
                    const itemValue = String(item[valueKey] ?? '')
                    const itemLabel = getItemLabel(item, labelKey, labelKeys)
                    const isSelected = value.includes(itemValue)
                    const isLastItem = infinite && index === items.length - 1

                    return (
                      <div
                        key={itemValue || index}
                        ref={(el) => {
                          if (isLastItem) {
                            lastItemRef.current = el
                            setLastItemElement(el)
                          }
                        }}
                      >
                        <CommandItem
                          className="cursor-pointer"
                          onSelect={() => handleToggle(itemValue, item)}
                          value={itemLabel}
                        >
                          {renderOption ? (
                            renderOption(item)
                          ) : getOptionLabel ? (
                            getOptionLabel(item)
                          ) : (
                            <span>{itemLabel}</span>
                          )}
                          <Check
                            className={cn(
                              'ms-auto h-4 w-4',
                              isSelected ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                        </CommandItem>
                      </div>
                    )
                  })}
                  {infinite && isFetchingNextPage && (
                    <div className="flex items-center justify-center py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-muted-foreground ms-2 text-sm">
                        {t('loading')}
                      </span>
                    </div>
                  )}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 ? (
        <div className="flex min-w-0 flex-wrap gap-2">
          {value.map((id) => {
            const item = cachedItemsMap.get(id)
            const itemLabel = item
              ? getItemLabel(item, labelKey, labelKeys)
              : id
            return (
              <Chip
                classNames={{
                  content:
                    'flex min-w-0 max-w-full items-center gap-2 rtl:flex-row-reverse sm:max-w-[220px]'
                }}
                handleClose={() => handleRemoveChip(id)}
                key={id}
                onClose={() => handleRemoveChip(id)}
                radius="full"
                variant="bordered"
                withCloseButton
              >
                {renderSelected ? (
                  renderSelected(item)
                ) : (
                  <span className="truncate">{itemLabel}</span>
                )}
              </Chip>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
